import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import type { PriceResponseDto } from '../../api-schema/generated/types';

/**
 * How old the *matched price* is, not how long ago a message arrived.
 *
 * The price hub publishes ~8 messages/second for the whole market and only
 * 7-16% of them carry a matched price, so a symbol can sit minutes between real
 * price changes while order-book messages keep flowing. Without this the UI
 * cannot tell "the price is not moving" from "the feed went stale", which is the
 * question users actually have when the screen looks frozen.
 * See fortuna-by-code-be/docs/WS-LATENCY-INVESTIGATION.md.
 *
 * The badge re-renders on its own clock — age grows when nothing arrives, which
 * is exactly the case worth showing.
 */

/** Above this the price is stale enough to warn about. */
const WARN_AFTER_MS = 15_000;
/** Above this it is stale enough that acting on it is a bad idea. */
const STALE_AFTER_MS = 60_000;

const TICK_MS = 1_000;

const Badge = styled.span<{ $level: 'fresh' | 'warn' | 'stale' }>`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  font-size: ${({ theme }) => theme.fontSize.xs};
  font-family: 'Courier New', monospace;
  white-space: nowrap;
  /* Order-book headers uppercase + letter-space their cells; the age is a value,
     not a label, so it opts out wherever it is mounted. */
  text-transform: none;
  letter-spacing: 0;
  color: ${({ theme, $level }) =>
        $level === 'stale' ? theme.colors.error
            : $level === 'warn' ? theme.colors.warning
                : theme.colors.text.tertiary};
`;

const Dot = styled.span<{ $level: 'fresh' | 'warn' | 'stale' }>`
  width: 6px;
  height: 6px;
  border-radius: 50%;
  flex-shrink: 0;
  background-color: ${({ theme, $level }) =>
        $level === 'stale' ? theme.colors.error
            : $level === 'warn' ? theme.colors.warning
                : theme.colors.trading.up};
`;

export const formatAge = (ms: number): string => {
    if (ms < 1_000) return 'live';
    if (ms < 60_000) return `${Math.floor(ms / 1_000)}s`;
    if (ms < 3_600_000) return `${Math.floor(ms / 60_000)}m${Math.floor((ms % 60_000) / 1_000)}s`;
    return `${Math.floor(ms / 3_600_000)}h${Math.floor((ms % 3_600_000) / 60_000)}m`;
};

interface PriceAgeProps {
    priceData?: PriceResponseDto | null;
    /** Prefix shown before the age, e.g. "price". Omit for the bare age. */
    label?: string;
}

const PriceAge: React.FC<PriceAgeProps> = ({ priceData, label }) => {
    const [, setTick] = useState(0);
    const stampUnix = priceData?.price_timestamp_unix ?? null;

    useEffect(() => {
        if (stampUnix == null) return;
        const id = window.setInterval(() => setTick((t) => t + 1), TICK_MS);
        return () => window.clearInterval(id);
    }, [stampUnix]);

    // No usable source stamp — say nothing rather than claim a freshness we
    // cannot back up (part of the feed publishes no timestamp at all).
    if (stampUnix == null) return null;

    const ageMs = Math.max(0, Date.now() - stampUnix * 1_000);
    const level = ageMs >= STALE_AFTER_MS ? 'stale' : ageMs >= WARN_AFTER_MS ? 'warn' : 'fresh';

    return (
        <Badge
            $level={level}
            title={`Last matched price from the source: ${new Date(stampUnix * 1_000).toLocaleTimeString()}`}
        >
            <Dot $level={level} />
            {label ? `${label} ` : ''}{formatAge(ageMs)}
        </Badge>
    );
};

export default PriceAge;
